import { useMemo } from 'react';

import { EntryTypeEnum } from 'generated/graphql-gateway';
import { selectors, useAppSelector } from 'state/global-state';
import { useUIContext } from 'state/ui';
import { useUnifiedMenuContext } from 'state/unified-menu';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { getComboSlotItemPrice, getFirstDefaultId, isItem } from 'state/unified-menu/utils';

import { DEFAULT_SLOT_KEY, SlotsSelections } from './types';
import { computeMenuItemPrice } from './utils';

export const useProductTotalPrice = (
  product: ILocalizedMenuEntry,
  slotsSelections: SlotsSelections,
  productQuantity: number,
  cartIdEditing?: string,
  rewardBenefitId?: string
) => {
  const { formatCurrencyForLocale } = useUIContext();
  const { posServiceMode } = useMenuQueryOptions();
  const { indexedMenu } = useUnifiedMenuContext();
  const appliedLoyaltyRewards = useAppSelector(selectors.loyalty.selectAppliedLoyaltyRewards);
  const availableLoyaltyRewardsMap = useAppSelector(
    selectors.loyalty.selectAvailableLoyaltyRewardsMap
  );
  const availablePoints = useAppSelector(selectors.loyalty.selectStagedCartPoints);

  const discountPrice = useMemo(() => {
    const individualItemPrice = product?.price?.[posServiceMode] ?? 0;
    if (appliedLoyaltyRewards[cartIdEditing ?? '']) {
      return individualItemPrice;
    }

    const reward = availableLoyaltyRewardsMap[product?.id];
    if (rewardBenefitId && reward) {
      const maxApplicationsByPoints = Math.floor(availablePoints / reward.pointCost);
      const timesApplied = Math.min(maxApplicationsByPoints, productQuantity);
      return individualItemPrice * timesApplied;
    }

    return 0;
  }, [
    appliedLoyaltyRewards,
    availableLoyaltyRewardsMap,
    availablePoints,
    cartIdEditing,
    posServiceMode,
    product?.id,
    product?.price,
    productQuantity,
    rewardBenefitId,
  ]);

  const totalPrice = useMemo(() => {
    return (
      computeTotalPrice(indexedMenu, posServiceMode, product, slotsSelections) * productQuantity -
      discountPrice
    );
  }, [indexedMenu, posServiceMode, product, slotsSelections, productQuantity, discountPrice]);

  const formattedTotalPrice = useMemo(() => formatCurrencyForLocale(totalPrice), [
    formatCurrencyForLocale,
    totalPrice,
  ]);

  return {
    totalPrice,
    formattedTotalPrice,
  };
};

function computeTotalPrice(
  indexedMenu: IndexedMenu,
  posServiceMode: PosServiceMode,
  selectedProduct: ILocalizedMenuEntry,
  slotsSelections: SlotsSelections
): number {
  if (isItem(selectedProduct)) {
    const modifierSelections = slotsSelections[DEFAULT_SLOT_KEY]?.modifiers ?? {};
    return computeMenuItemPrice(
      indexedMenu,
      selectedProduct,
      posServiceMode,
      undefined,
      modifierSelections
    );
  }

  let comboPrice = selectedProduct?.price?.[posServiceMode] ?? 0;
  for (const comboSlotRef of selectedProduct?.options?.entries ?? []) {
    const comboSlot = indexedMenu.get(comboSlotRef.entryId);
    const defaultComboSlotPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, undefined);

    let item;
    let contextualPrice;
    const slotSelection = slotsSelections[comboSlot.id];
    if (slotSelection?.itemId) {
      item = indexedMenu.get(slotSelection.itemId, { type: EntryTypeEnum.ITEM });
      contextualPrice = getComboSlotItemPrice(indexedMenu, comboSlot.id, slotSelection.itemId);
    } else {
      item = indexedMenu.get(getFirstDefaultId(comboSlot));
      contextualPrice = defaultComboSlotPrice;
    }

    const modifierSelections = slotSelection?.modifiers ?? {};
    const itemPrice = computeMenuItemPrice(
      indexedMenu,
      item,
      posServiceMode,
      contextualPrice,
      modifierSelections
    );

    // Only add price for upsell combo slots or customizations
    const defaultPrice = defaultComboSlotPrice?.[posServiceMode] ?? 0;
    if (itemPrice !== defaultPrice) {
      comboPrice += itemPrice;
    }
  }
  return comboPrice;
}
